import { useEffect, useState } from 'react';
import { OrderComment } from '../../api/ShopClientsAPI';
import { CommissionComment } from '../../api/ComissionContractsAPI';
import { PrivateSharedResourceLink } from '../PrivateSharedResourceLink/PrivateSharedResourceLink';
import { Icon } from '../Icon/Icon';
import fileIcon from '../../assets/images/file.svg';
import { Trash2 } from 'lucide-react';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

function isWithinLast5Minutes(creationTime: number | string | undefined, now: number = Date.now()): boolean {
    if (creationTime == null) return false;
    const date = new Date(creationTime);
    return now - date.getTime() < FIVE_MINUTES_MS;
}

type Props = {
    comment: OrderComment | CommissionComment;
    type?: 'order' | 'commission';
    isSelf?: boolean;
    orderId?: number;
    onDelete?: (commentId: number) => void;
}

const CommentCard = ({ comment, type = 'order', isSelf = false, orderId, onDelete }: Props) => {
    const [now, setNow] = useState(() => Date.now());

    const isOrderComment = type === 'order';
    const orderComment = isOrderComment ? comment as OrderComment : null;
    const commissionComment = !isOrderComment ? comment as CommissionComment : null;

    const text = isOrderComment ? orderComment?.comment : commissionComment?.comment;
    const creationTime = isOrderComment ? orderComment?.creationTime : commissionComment?.creationTime;
    const author = isOrderComment ? orderComment?.shopName : commissionComment?.writtenBy;
    const attachmentUrls = isOrderComment ? (orderComment?.attachmentUrls ?? []) : [];

    const isOwnComment = orderComment?.self === true || orderComment?.isSelf === true;
    const withinWindow = isWithinLast5Minutes(orderComment?.creationTime, now);
    const canDelete = isOrderComment && orderComment?.id != null && isOwnComment && withinWindow && onDelete != null;

    useEffect(() => {
        if (!withinWindow || creationTime == null) return;
        const createdAt = new Date(creationTime).getTime();
        const expiresAt = createdAt + FIVE_MINUTES_MS;
        const msUntilExpiry = expiresAt - Date.now();
        if (msUntilExpiry <= 0) return;
        const timeoutId = setTimeout(() => setNow(Date.now()), msUntilExpiry);
        return () => clearTimeout(timeoutId);
    }, [creationTime, withinWindow]);

    const sideBarColor = isSelf ? 'border-gold-500' : 'border-gold-600';
    const formattedDate = new Date(creationTime!).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });

    return (
        <div className={`bg-white shadow rounded-lg p-4 animate-fade-in-up relative border-l-4 ${sideBarColor}`}>
            <div className="flex justify-between items-center mb-2">
                <h4 className="text-lg font-semibold">{author}</h4>
                <span className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{formattedDate}</span>
                    {canDelete && (
                        <button
                            type="button"
                            onClick={() => onDelete!(orderComment!.id!)}
                            className="text-red-600 hover:text-red-800 p-1 rounded aria-label"
                            aria-label="Delete comment"
                        >
                            <Trash2 className="w-4 h-4" aria-hidden />
                        </button>
                    )}
                </span>
            </div>
            <p className="text-base text-gray-800">{text}</p>
            {attachmentUrls.length > 0 && orderId != null && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {attachmentUrls.map((fileName, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1">
                            <Icon url={fileIcon} cssStyles="w-4" />
                            <PrivateSharedResourceLink orderId={orderId} file={fileName} />
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CommentCard;
